import { useMemo, useState, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { Sparkles } from 'lucide-react';
import {
  eventTypeLabel,
  useActiveSeason,
  useSetSeasonStatus,
  type SeasonPlan,
} from '@/lib/season';
import { ConfirmDialog } from '@/features/settings/ConfirmDialog';
import { NewSeasonForm } from './NewSeasonForm';
import { SeasonTimeline } from './SeasonTimeline';

/**
 * Bloque CTA pequeño que dirige al usuario al **Coach IA semanal**
 * (`/coach/nuevo-plan`). Se muestra en todos los estados de SeasonPage
 * porque desde M2.16 el link al Coach NO está en el nav top (el item
 * "Plan" del nav apunta directo a `/coach/temporada`). Sin este botón el
 * Coach quedaba accesible sólo por URL directa.
 */
function CoachWeeklyCta(): ReactElement {
  return (
    <Link
      to="/coach/nuevo-plan"
      className="inline-flex items-center gap-1.5 rounded-md border border-teal-300 bg-teal-50 px-3 py-1.5 text-sm font-medium text-teal-800 hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1"
    >
      <Sparkles aria-hidden="true" className="h-4 w-4" />
      Coach IA semanal
    </Link>
  );
}

/**
 * Página `/coach/temporada` (M2.2).
 *
 * Estados:
 * - Loading: spinner discreto.
 * - Error: banner rojo (probable 401 → el root router lo gestiona).
 * - Sin plan activo (null): `<NewSeasonForm />`.
 * - Con plan activo: header (name, event_type, event_date, semanas hasta el
 *   evento) + `<SeasonTimeline />` + acciones (archivar, crear nuevo).
 *
 * Decisión "crear nuevo plan" con plan activo:
 *   Toggle local `creatingNew` que oculta la timeline y muestra el form. Al
 *   completar, la mutación invalida `['season', 'active']` y el componente
 *   se re-renderiza con el plan nuevo automáticamente. No redirige a otra
 *   ruta — todo ocurre en la misma pantalla.
 */
export function SeasonPage(): ReactElement {
  const query = useActiveSeason();
  const setStatusMutation = useSetSeasonStatus();
  const [creatingNew, setCreatingNew] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  if (query.isLoading) {
    return (
      <div role="status" aria-live="polite" className="text-sm text-slate-600">
        Cargando plan de temporada…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
        No se pudo cargar el plan de temporada. {query.error?.message ?? ''}
      </div>
    );
  }

  const plan = query.data ?? null;

  // Sin plan → form directo.
  if (!plan) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-end">
          <CoachWeeklyCta />
        </div>
        <NewSeasonForm
          onResult={() => {
            // La query se invalida sola via useCreateSeasonPlan; nada que hacer.
          }}
        />
      </div>
    );
  }

  // Con plan + el usuario quiere crear uno nuevo → mostramos solo el form
  // (con botón para cancelar y volver a la timeline).
  if (creatingNew) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Al crear un nuevo plan, el actual{' '}
            <strong className="text-slate-900">{plan.name}</strong> se archivará
            automáticamente.
          </p>
          <button
            type="button"
            onClick={() => setCreatingNew(false)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            Cancelar
          </button>
        </div>
        <NewSeasonForm
          onResult={() => {
            // Tras éxito, la query refresca; salimos del modo "creatingNew"
            // para volver a ver la timeline con el plan nuevo.
            setCreatingNew(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SeasonHeader plan={plan} />
      <SeasonTimeline plan={plan} />

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
        <CoachWeeklyCta />
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setConfirmArchive(true)}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            Archivar plan
          </button>
          <button
            type="button"
            onClick={() => setCreatingNew(true)}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            Crear nuevo plan
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmArchive}
        title="Archivar plan de temporada"
        description={
          <span>
            ¿Seguro que quieres archivar <strong>{plan.name}</strong>? El plan
            quedará accesible en el histórico pero ya no se usará para
            contextualizar tus planes semanales.
          </span>
        }
        confirmLabel={setStatusMutation.isPending ? 'Archivando…' : 'Archivar'}
        destructive
        isPending={setStatusMutation.isPending}
        onCancel={() => {
          if (setStatusMutation.isPending) return;
          setConfirmArchive(false);
        }}
        onConfirm={() => {
          if (setStatusMutation.isPending) return;
          setStatusMutation.mutate(
            { id: plan.id, status: 'archived' },
            {
              onSuccess: () => setConfirmArchive(false),
            },
          );
        }}
      />

      {setStatusMutation.isError && (
        <div role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo archivar el plan. {setStatusMutation.error?.message ?? ''}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function SeasonHeader({ plan }: { plan: SeasonPlan }): ReactElement {
  const weeksUntilEvent = useMemo(() => weeksUntil(plan.eventDateISO), [plan.eventDateISO]);
  const isPast = weeksUntilEvent !== null && weeksUntilEvent < 0;

  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          {plan.name}
        </h1>
        <p className="text-sm text-slate-600">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-800">
            {eventTypeLabel(plan.eventType)}
          </span>
        </p>
      </div>
      <p className="text-sm text-slate-600">
        Evento:{' '}
        <strong className="text-slate-900">{formatEventDate(plan.eventDateISO)}</strong>
        {weeksUntilEvent !== null && (
          <>
            {' · '}
            {isPast ? (
              <span className="text-slate-700">
                El evento ya tuvo lugar (hace {Math.abs(weeksUntilEvent)} semana
                {Math.abs(weeksUntilEvent) === 1 ? '' : 's'}).
              </span>
            ) : (
              <span className="text-slate-700">
                {weeksUntilEvent === 0
                  ? 'Esta semana es el evento'
                  : `Faltan ${weeksUntilEvent} semana${weeksUntilEvent === 1 ? '' : 's'}`}
              </span>
            )}
          </>
        )}
      </p>
      {plan.goalDescription && (
        <p className="text-sm italic text-slate-600">"{plan.goalDescription}"</p>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Semanas hasta el evento, redondeando hacia abajo. Negativo si ya pasó.
 * Null si la fecha no parsea.
 */
function weeksUntil(eventDateISO: string): number | null {
  const normalized = eventDateISO.includes('T')
    ? eventDateISO
    : `${eventDateISO}T00:00:00Z`;
  const t = Date.parse(normalized);
  if (!Number.isFinite(t)) return null;
  const diffMs = t - Date.now();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days >= 0) {
    return Math.floor(days / 7);
  }
  // Pasado: -floor(abs/7) para que -3 días = 0 semanas, -8 días = -1 semana.
  return -Math.ceil(Math.abs(days) / 7);
}

function formatEventDate(iso: string): string {
  const normalized = iso.includes('T') ? iso : `${iso}T00:00:00Z`;
  const t = Date.parse(normalized);
  if (!Number.isFinite(t)) return iso;
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(t));
  } catch {
    return iso;
  }
}
